import { useReducer as useReducerInternal, useMemo, useEffect } from 'react';

const stores = {};
const subscribers = {};

const withDevTools = name => (
  name
    && process.env.NODE_ENV !== 'production'
    && typeof 'window' !== 'undefined'
    && window.__REDUX_DEVTOOLS_EXTENSION__
);

const devToolsReducer = reducer => (state, action) => {
  if (action.type === 'REDUX_DEVTOOLS_SET_STATE') {
    return action.state;
  }

  return reducer(state, action);
};

/*
 * This useReducer will allow you to use the Redux devtools in order to view reducer state and dispatch actions without the
 * need to have Redux as a dependency.
 *
 * Taken from https://blog.solutelabs.com/configuring-thunk-action-creators-and-redux-dev-tools-with-reacts-usereducer-hook-5a1608476812
 */
export default function useReducer(reducer, initState, name, lazyInit) {
  const shouldConfigDevtools = withDevTools(name);
  const memoizedReducer = useMemo(() => {
    if (shouldConfigDevtools) {
      return devToolsReducer(reducer);
    }

    return reducer;
  }, [reducer, shouldConfigDevtools]);

  const [state, dispatch] = useReducerInternal(memoizedReducer, initState, lazyInit);

  useEffect(() => {
    if (!shouldConfigDevtools) {
      return;
    }

    if (stores[name]) {
      throw new Error('More than one useReducer have the same name');
    }

    let initialState = initState;
    if (lazyInit) {
      initialState = lazyInit(initState);
    }

    stores[name] = window.__REDUX_DEVTOOLS_EXTENSION__(
      reducer,
      initialState,
      {
        name,
      },
    );

    subscribers[name] = stores[name].subscribe(() => {
      dispatch({ type: 'REDUX_DEVTOOLS_SET_STATE', state: stores[name].getState() });
    });

    return () => {
      subscribers[name]();
      delete subscribers[name];
      delete stores[name];
    };
  }, [dispatch, initState, lazyInit, name, reducer, shouldConfigDevtools]);

  const customDispatch = (action) => {
    if (shouldConfigDevtools) {
      stores[name].dispatch(action);
    } else {
      dispatch(action);
    }
  };

  return [state, customDispatch];
}
